import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import CartItem from "../../components/home/cartComponents/CartItem";
import EmptyCart from "./cartComponents/EmptyCart";
import { useNavigate } from "react-router-dom";
import SectionHeader from "../../components/home/membershipPlanComponents/SectionHeader";
import { fetchCart, removeFromCartAsync } from "../../features/cartSlice";
import { createNewOrder } from "../../features/orderSlice"; // Assuming you have this action for creating orders
import { fetchCurrentSubscription } from "../../features/subscriptionSlice";
import { fetchUserOrders } from "../../features/orderSlice";
import toast, { Toaster } from "react-hot-toast";

export default function Cart() {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user?.userId);
  const { items, status, error } = useSelector((state) => state.cart);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const navigate = useNavigate();
  const [limitExceededCount, setLimitExceededCount] = useState(null);
  const [showLimitExceededModal, setShowLimitExceededModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  useEffect(() => {
    if (items && items.length > 0) {
      const availableBooks = items
        .filter((item) => item.book.bookStockStatus !== "OUT_OF_STOCK")
        .map((item) => item.book.bookId);
      setSelectedBooks(availableBooks);
    }
  }, [items]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchCart(userId));
    }
  }, [userId, dispatch]);

  const handleRemove = (bookId) => {
    dispatch(removeFromCartAsync({ userId, bookId }));
  };
  console.log("Cart items: ", items); // Debugging line to check cart items
  const handleSelect = (book) => {
    if (book.bookStockStatus === "OUT_OF_STOCK") {
      toast.error("This book is currently out of stock.");
      return;
    }

    const bookId = book.bookId;

    setSelectedBooks((prevSelectedBooks) => {
      if (prevSelectedBooks.includes(bookId)) {
        return prevSelectedBooks.filter((id) => id !== bookId);
      } else {
        return [...prevSelectedBooks, bookId];
      }
    });
  };

  const handleProceedToRent = async () => {
    if (selectedBooks.length === 0) {
      toast.error("Please select books to rent.");
      return;
    }

    // 1. Check if user is logged in
    if (!userId) {
      toast.error("Please log in to proceed.");
      navigate("/login");
      return;
    }

    try {
      // 2. Fetch user's current subscription
      const subscriptionResult = await dispatch(
        fetchCurrentSubscription(userId)
      ).unwrap();
      const subscription = subscriptionResult?.subscription;

      if (!subscription || subscription.status !== "ACTIVE") {
        setShowSubscriptionModal(true); // ✅ Trigger the modal
        return;
      }

      if (
        subscription.remainingDays !== undefined &&
        subscription.remainingDays <= 0
      ) {
        toast.error("Your subscription has expired.");
        navigate("/membershipPlans");
        return;
      }

      // 3. Determine allowed limit based on plan
      const packageType = subscription.packageType;
      const allowedBookLimit =
        {
          ONE_BOOK: 1,
          TWO_BOOKS: 2,
          THREE_BOOKS: 3,
        }[packageType] || 0;

      // 4. Fetch user active orders
      const userOrdersResult = await dispatch(fetchUserOrders(userId)).unwrap();
      const activeOrdersCount = (userOrdersResult || []).filter(
        (o) => o?.returnStatus === "NOT_RETURNED"
      ).length;

      const remainingLimit = allowedBookLimit - activeOrdersCount;

      // 5. Check if selected books exceed remaining limit
      if (selectedBooks.length > remainingLimit) {
        setLimitExceededCount(remainingLimit);
        setShowLimitExceededModal(true);

        return;
      }

      // ✅ Pass bookIds in query string and navigate
      const queryParam = selectedBooks.join(",");
      navigate(`/multi-book-order?bookIds=${queryParam}`);
    } catch (err) {
      console.error("Failed during proceed to rent check:", err);
      toast.error("Failed to verify subscription or order limit.");
    }
  };

  if (status === "loading") {
    return <div className="p-8">Loading...</div>;
  }

  if (status === "failed") {
    return <div className="p-8 text-red-500">{error}</div>;
  }

  return (
    <section className="w-full">
      <div className="w-full">
        <SectionHeader title="My Cart" />
      </div>

      {!items || items.length === 0 ? (
        <div className="mt-6">
          <EmptyCart />
        </div>
      ) : (
        <>
          <div className="mt-6 space-y-2">
            {items.map((bookWrapper) => {
              const book = bookWrapper.book;
              return (
                <div key={book.bookId} className="max-w-3xl mx-auto relative">
                  {/* Checkbox at top-right */}
                  <div className="absolute top-2 right-2 z-10 flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedBooks.includes(book.bookId)}
                      onChange={() => handleSelect(book)}
                      disabled={book.bookStockStatus === "OUT_OF_STOCK"}
                      className="w-5 h-5 mr-1 accent-primary disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>

                  <CartItem
                    book={{
                      id: book.bookId,
                      title: book.bookName,
                      author: book.author?.authorName || "Unknown Author",
                      publisher: book.bookPublication,
                      isbn: book.bookIsbn13 || book.bookIsbn10,
                      imageUrl: book.bookImages,
                      rating: book.averageRating || 0,
                      stock: book.bookStockStatus
                    }}
                    onRemove={() => handleRemove(book.bookId)}
                  />
                </div>
              );
            })}
          </div>

          {/* Proceed to Rent for selected books */}
          <div className="pt-8 flex flex-col items-center space-y-4 py-4">
              <>
                <button
                  onClick={handleProceedToRent}
                  disabled={selectedBooks.length === 0}
                  className={`inline-flex items-center px-6 py-3 rounded-2xl text-white shadow-3d transition duration-300
                 ${
                   selectedBooks.length === 0
                     ? "bg-gray-400 cursor-not-allowed"
                     : "bg-primary hover:bg-primary-shade4"
                 }
               `}
                >
                  Proceed to Rent ({selectedBooks.length} books)
                </button>

                {selectedBooks.length > 0 && (
                  <button
                    onClick={() => setSelectedBooks([])}
                    className="inline-flex items-center px-6 py-2 rounded-xl bg-red-100 text-red-600 hover:bg-red-200 transition text-sm font-medium"
                  >
                    Clear Selection
                  </button>
                )}
              </>

            <Link
              to="/explore-books"
              className="inline-flex items-center px-6 py-3 rounded-2xl bg-white border border-primary text-primary hover:bg-primary-shade1 hover:text-primary shadow-md transition duration-300"
            >
              <ArrowLeft size={18} className="mr-2" />
              Add More Books
            </Link>
          </div>
        </>
      )}
      {showLimitExceededModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold mb-2 text-red-600">
              Limit Reached
            </h2>
            <p className="text-gray-700 mb-4">
              You’ve selected more books than allowed in your current plan. You
              can rent only <strong>{limitExceededCount}</strong> more book
              {limitExceededCount > 1 ? "s" : ""}.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              We know you love reading — upgrade your plan to unlock more books
              and never run out of stories!
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowLimitExceededModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <Link
                to="/membershipPlans"
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-shade4"
                onClick={() => setShowLimitExceededModal(false)}
              >
                Upgrade Plan
              </Link>
            </div>
          </div>
        </div>
      )}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold mb-2 text-red-600">
              No Active Subscription
            </h2>
            <p className="text-gray-700 mb-4">
              Looks like you don’t have an active subscription. You need one to
              rent books.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Unlock the world of stories – choose a plan that fits your reading
              appetite!
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowSubscriptionModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <Link
                to="/membershipPlans"
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-shade4"
                onClick={() => setShowSubscriptionModal(false)}
              >
                View Plans
              </Link>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
